import { useTranslation } from "react-i18next";
import styled from "styled-components";

import ToggleSwitch from "../../components/ToggleSwitch";
import CloseIcon from "../../icons/Close";
import PlusIcon from "../../icons/Plus";
import { AgendaWeek } from "../../services/agenda";
import { FieldError } from "./styles";
import { TimePicker } from "./TimePicker";

const prefix = "pages.agenda.settings.week";

/** Monday first, as people read a week; the API's days are "0" (Sunday) to "6". */
export const WEEK_DAYS = ["1", "2", "3", "4", "5", "6", "0"] as const;

const DEFAULT_INTERVAL: [string, string] = ["09:00", "18:00"];

/** The most intervals a day takes (the API's rule). */
const MAX_INTERVALS = 6;

/**
 * Closing at midnight: the pickers have no 24:00, so "00:00" as the end of an interval is the end of the day, which
 * the API calls "24:00" (and which keeps the last hour bookable: a 23:59 closing left it out).
 */
const MIDNIGHT = "00:00";
export const toApiEnd = (time: string) => (time === MIDNIGHT ? "24:00" : time);
export const fromApiEnd = (time: string) => (time === "24:00" ? MIDNIGHT : time);
/** An end as it compares with a start: midnight comes after everything else in the day. */
const endOrder = (time: string) => (time === MIDNIGHT ? "24:00" : time);
/** Nothing fits after it: midnight, or the day's last minute. */
const closesTheDay = (time: string) => time === MIDNIGHT || time >= "23:59";

const addHour = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours >= 23 ? MIDNIGHT : `${String(hours + 1).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/** The other days that are on take Monday's hours (the closed ones stay closed). */
export const copyMonday = (week: AgendaWeek): AgendaWeek => {
  const monday = week["1"] ?? [];
  return Object.fromEntries(
    Object.entries(week).map(([day, intervals]) => [
      day,
      day !== "1" && intervals.length > 0 ? monday.map(([from, to]) => [from, to] as [string, string]) : intervals,
    ])
  );
};

/** Each interval ends after it starts and never crosses the one before (the API's rule). */
export const isDayValid = (intervals: [string, string][]) =>
  intervals.every(
    ([from, to], index) =>
      !!from && !!to && from < endOrder(to) && (index === 0 || from >= endOrder(intervals[index - 1][1]))
  );

type Props = {
  week: AgendaWeek;
  disabled: boolean;
  /** The days whose intervals don't hold (shown after a save was tried). */
  invalidDays: string[];
  onChange: (week: AgendaWeek) => void;
};

/** The hours of each day: on or off, and one or more intervals (e.g. a lunch break between two). */
export const WeekHours = ({ week, disabled, invalidDays, onChange }: Props) => {
  const { t } = useTranslation();
  const setDay = (day: string, intervals: [string, string][]) => onChange({ ...week, [day]: intervals });

  return (
    <Days>
      {WEEK_DAYS.map(day => {
        const intervals = week[day] ?? [];
        const name = t(`${prefix}.days.${day}`);
        const open = intervals.length > 0;
        return (
          // one line per day: the switch, its name, its intervals side by side, "+"
          <div key={day} role="group" aria-label={name} className={open ? "day" : "day closed"}>
            <ToggleSwitch
              value={open}
              disabled={disabled}
              label={name}
              setValue={on => setDay(day, on ? [DEFAULT_INTERVAL] : [])}
            />
            <span className="name">{name}</span>
            <div className="intervals">
              {!open && <span className="off">{t(`${prefix}.closed`)}</span>}
              {/* the intervals one under the other, "+" beside them all (in the middle of the day's line) */}
              {open && (
                <div className="list">
                  {intervals.map(([from, to], index) => (
                    <span key={index} className="interval">
                      <TimePicker
                        label={t(`${prefix}.from`)}
                        value={from}
                        disabled={disabled}
                        onChange={value =>
                          setDay(
                            day,
                            intervals.map((item, i) => (i === index ? [value, item[1]] : item))
                          )
                        }
                      />
                      <span aria-hidden="true">–</span>
                      <TimePicker
                        label={t(`${prefix}.to`)}
                        value={to}
                        disabled={disabled}
                        onChange={value =>
                          setDay(
                            day,
                            intervals.map((item, i) => (i === index ? [item[0], value] : item))
                          )
                        }
                      />
                      {!disabled && intervals.length > 1 && (
                        <button
                          type="button"
                          className="remove"
                          aria-label={t(`${prefix}.removeInterval`)}
                          onClick={() =>
                            setDay(
                              day,
                              intervals.filter((_, i) => i !== index)
                            )
                          }>
                          <CloseIcon width={8} height={8} color="currentColor" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {/* none after the end of the day, nor past the most a day takes */}
              {open &&
                !disabled &&
                intervals.length < MAX_INTERVALS &&
                !closesTheDay(intervals[intervals.length - 1][1]) && (
                  <button
                    type="button"
                    className="add"
                    aria-label={t(`${prefix}.addInterval`)}
                    title={t(`${prefix}.addInterval`)}
                    onClick={() => {
                      const start = intervals[intervals.length - 1][1];
                      setDay(day, [...intervals, [start, addHour(start)]]);
                    }}>
                    <PlusIcon width={12} height={12} color="currentColor" />
                  </button>
                )}
            </div>
            {invalidDays.includes(day) && (
              <FieldError role="alert" className="error">
                {t(`${prefix}.invalid`)}
              </FieldError>
            )}
          </div>
        );
      })}
    </Days>
  );
};

const Days = styled.div`
  display: flex;
  flex-direction: column;
  font-family: Inter, sans-serif;

  .day {
    display: grid;
    grid-template-columns: 40px 5.5rem minmax(0, 1fr);
    gap: 0.5rem 0.875rem;
    align-items: center;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--color-neutral-base);
  }

  .day:first-of-type {
    padding-top: 0;
  }

  .day:last-of-type {
    padding-bottom: 0;
    border-bottom: 0;
  }

  /* the toggle's label is sized by its track: the track's box, without the stacked forms' margin */
  .day > label {
    width: 40px;
    height: 24px;
    margin: 0;
    padding: 0;

    input:not(:checked) ~ span {
      background-color: var(--color-neutral-base);
    }
  }

  .name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--color-neutral-white);
  }

  .closed .name {
    color: var(--color-neutral-150);
  }

  .intervals {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .off {
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .interval {
    display: inline-flex;
    gap: 0.375rem;
    align-items: center;
    color: var(--color-neutral-150);
  }

  .remove {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 1rem;
    color: var(--color-neutral-150);
    border-radius: 999px;
  }

  .remove:hover {
    color: var(--color-neutral-white);
  }

  .add {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    color: var(--color-brand-plus);
    border: 1px dashed var(--color-neutral-base);
    border-radius: 999px;
  }

  .add:hover {
    border-color: var(--color-brand-plus);
  }

  .error {
    grid-column: 3;
    margin: 0;
  }

  /* phones: the switch and the name on top, the intervals under them */
  @media (max-width: 640px) {
    .day {
      grid-template-columns: 40px minmax(0, 1fr);
    }

    .intervals,
    .error {
      grid-column: 1 / -1;
    }
  }
`;
